/**
 * debugReportRedact — GDPR redaction of a diagnostic bundle before it leaves
 * the device (submit OR download).
 *
 * WHY (no shared util existed): the bug-report bundle historically shipped the
 * RAW console stream, RAW runtime-error stacks, RAW recent-activity payloads
 * (typed `unknown` — customer lead PII flows through here), RAW localStorage
 * values, and RAW request URLs. A user pasting a token into the comment box, an
 * `Authorization: Bearer …` line that leaked into `console.error`, or a lead's
 * e-mail / phone / IBAN in an activity payload all left the device verbatim.
 * That is a DSGVO/GDPR exposure. There is no pre-existing redaction helper in
 * the repo (`githubPat.mask` is an SL display string; `authSession.sanitizeUrl`
 * strips OAuth params off the address bar) so this module is the single egress
 * scrubber for the debug-report bundle.
 *
 * WHAT is redacted (free-form / uncontrolled fields only):
 *   - console messages, runtime-error message + stack
 *   - recent-activity payloads (deep walk of the `unknown` shape)
 *   - localStorage snapshot values
 *   - last-save-attempt + network-request URLs (query secrets stripped)
 *   - the user's free-text comment
 * Secrets removed from strings: JWTs, `Bearer …` tokens, `token=`/`password=`
 * style key/value pairs, e-mail addresses, IBANs, and international / German
 * phone numbers. Object keys whose NAME is sensitive (email/phone/token/…) have
 * their value replaced wholesale.
 *
 * WHAT is deliberately KEPT: the reporter's own identity (`user.email`/`id`) —
 * that is the consented "who filed this" needed for triage (a required
 * dimension of the report), NOT incidental third-party PII — plus the
 * structured diagnostic scalars (`env`, `auth` booleans, `browser`, `route`,
 * `timestamp`, `bugRef`, `buildHash`) and the `screenshot` data-URL (redacting
 * a 500 KB base64 blob by regex would both corrupt the image and never find a
 * real secret). Those fields are copied through untouched.
 *
 * Idempotent: redacting already-redacted content is a no-op, so it is safe to
 * run at collect time (so the PREVIEW shows exactly what will be sent) AND
 * again at submit/download (to catch the comment attached after collect).
 */
import type { DiagnosticData } from './debugReportCapture.types'

// ── String-level secret patterns ─────────────────────────────────────────

// A three-segment JWT (`eyJ…header.eyJ…payload.signature`). The signature
// segment may be empty (`alg:none`), hence `*` on the last group.
const JWT_RE = /eyJ[A-Za-z0-9_=-]+\.[A-Za-z0-9_=-]+\.[A-Za-z0-9_=-]*/g
// `Bearer <token>` / `Basic <b64>` (incl. after an `Authorization:` label).
// Run BEFORE the key/value rule so the scheme keyword survives and the token
// after it is scrubbed — a naïve `authorization=<v>` rule would otherwise
// consume only the word "Bearer" and leave the real token exposed.
const SCHEME_TOKEN_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi
// `token=…`, `access_token: "…"`, `password=…`, etc. Captures the key + the
// assignment glue + an optional quote so we can rebuild `key=[REDACTED]`.
// NOTE: `authorization` is deliberately NOT here — its value is a scheme+token
// handled by SCHEME_TOKEN_RE (above) / JWT_RE, which redact the token itself
// rather than mistaking the scheme keyword for the secret.
const KV_SECRET_RE =
  /(access_token|refresh_token|api[_-]?key|apikey|password|passwd|secret|token)(\s*[=:]\s*)("?)([A-Za-z0-9._~+/=-]{6,})(\3)/gi
// A token as it travels in a URL query or fragment: `<anything>token=<value>` up to the next `&`,
// `#`, quote or whitespace. KV_SECRET_RE alone stops at the first character outside its value
// alphabet, so a PERCENT-ENCODED token (`provider_refresh_token=1%2F%2F0g…` — an OAuth provider's
// refresh token in the callback fragment) kept everything after the first `%`, or was not matched
// at all when fewer than 6 characters preceded it. Runs BEFORE KV_SECRET_RE; the unquoted `=`
// form only, so `token: "…"` and `token="…"` stay with the rule above.
const URL_PARAM_TOKEN_RE = /([A-Za-z_]*token)=([^&\s"'<>#]+)/gi
// E-mail address — also with its `@` percent-encoded (`scout%40clanda.de`), which is how
// encodeURIComponent puts it into a request URL.
const EMAIL_RE =/[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
// IBAN (2 letters, 2 digits, then 10-30 alphanumerics, optional spacing).
const IBAN_RE = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]){10,30}\b/g
// International phone: `+…` or `00…` followed by ≥8 more digits/separators.
const PHONE_INTL_RE = /(?:\+|00)\d[\d\s./()-]{7,}\d/g
// German domestic phone: `0` + area + subscriber (kept conservative so plain
// numeric IDs are not mistaken for phone numbers).
const PHONE_DE_RE = /\b0\d{2,4}[\s/-]?\d{5,10}\b/g

/**
 * Redact secrets + PII out of a single free-form string. Order matters:
 * key/value + Bearer forms run first so a token is scrubbed WITH its label,
 * then bare JWT, then the PII classes.
 */
export function redactString(input: string): string {
  return input
    .replace(SCHEME_TOKEN_RE, (_m, scheme: string) => `${scheme} [REDACTED]`)
    .replace(URL_PARAM_TOKEN_RE, (_m, key: string) => `${key}=[REDACTED]`)
    .replace(KV_SECRET_RE, (_m, key: string, glue: string, quote: string) => `${key}${glue}${quote}[REDACTED]${quote}`)
    .replace(JWT_RE, '[REDACTED_JWT]')
    .replace(IBAN_RE, '[REDACTED_IBAN]')
    .replace(EMAIL_RE, '[REDACTED_EMAIL]')
    .replace(PHONE_INTL_RE, '[REDACTED_PHONE]')
    .replace(PHONE_DE_RE, '[REDACTED_PHONE]')
}

// ── Object-level redaction ───────────────────────────────────────────────

/** Object keys whose VALUE is sensitive regardless of content. */
const SENSITIVE_KEYS = new Set<string>([
  'email', 'mail', 'e_mail', 'phone', 'tel', 'telefon', 'mobile', 'handy',
  'iban', 'bic', 'vat', 'ustid', 'taxid', 'ssn',
  'password', 'passwd', 'passwort', 'secret', 'token', 'access_token',
  'refresh_token', 'authorization', 'apikey', 'api_key', 'cookie', 'session',
])

/** Query-parameter names whose value is a secret and must be dropped. Shared with lib/sentryScrub. */
export const SENSITIVE_QUERY =new Set<string>([
  'access_token', 'refresh_token', 'token', 'apikey', 'api_key', 'key',
  'code', 'password', 'secret', 'sig', 'signature', 'jwt', 'auth',
])

/** Deep-redact an arbitrary (`unknown`) value — used for activity payloads. */
export function redactDeep(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return redactString(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.map(redactDeep)
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : redactDeep(v)
    }
    return out
  }
  return value
}

/**
 * Redact a URL: strip secret query-parameter values, and scrub the rest for
 * any inline JWT / e-mail. Handles relative URLs (no `URL` constructor, which
 * throws on a bare pathname).
 */
export function redactUrl(url: string): string {
  const qIdx = url.indexOf('?')
  if (qIdx < 0) return redactString(url)
  const base = url.slice(0, qIdx)
  const query = url.slice(qIdx + 1)
  const redactedQuery = query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=')
      if (eq < 0) return pair
      const key = pair.slice(0, eq)
      if (SENSITIVE_QUERY.has(key.toLowerCase())) return `${key}=REDACTED`
      return `${key}=${redactString(pair.slice(eq + 1))}`
    })
    .join('&')
  return `${redactString(base)}?${redactedQuery}`
}

/**
 * Redact a whole diagnostic bundle before egress. Only the free-form /
 * uncontrolled fields are touched; the reporter identity + structured
 * diagnostic scalars + screenshot are copied through unchanged (see file
 * header). Idempotent.
 */
export function redactDiagnostics(data: DiagnosticData): DiagnosticData {
  return {
    ...data,
    userComment: data.userComment === null ? null : redactString(data.userComment),
    console: data.console.map((e) => ({ ...e, message: redactString(e.message) })),
    runtimeErrors: data.runtimeErrors.map((e) => ({
      ...e,
      message: redactString(e.message),
      stack: e.stack === null ? null : redactString(e.stack),
    })),
    recentActivity: data.recentActivity.map((a) => ({ ...a, payload: redactDeep(a.payload) })),
    localStorage: Object.fromEntries(
      Object.entries(data.localStorage).map(([k, v]) => [k, v === null ? null : redactString(v)]),
    ),
    lastSaveAttempts: data.lastSaveAttempts.map((a) => ({ ...a, url: redactUrl(a.url) })),
    networkRequests: data.networkRequests.map((n) => ({ ...n, url: redactUrl(n.url) })),
  }
}
